import { CalendarDays, LayoutGrid, Mars, Trophy, Users, Venus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  buildPublicNameMap,
  buildScheduleTimeline,
  LiveMatchCard,
  MatchScoreRow,
  recentWithinDays,
  roundAccent,
  roundLabel,
  ScheduleGrid,
  screenProgress,
  shortTime,
  splitScreenMatches,
  todayIso,
  toBracketMatchView,
  UpcomingMatchMeta,
  usePublicCourtSchedulesBatchQuery,
  usePublicMatchesBatchQuery,
  type ApiClubCourt,
  type ApiMatch,
  type ApiMatchPlanner,
  type ApiPlannerMatch,
  type PublicClubCompetitions,
  type ScheduleCourt,
  type ScheduleTimeline,
} from '@/features/competition';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardHeader,
  DateInput,
  EmptyState,
  Skeleton,
  StatTile,
} from '@/shared/ui';

/** Rozstawienia nie ma w publicznym API — widoki rysują się bez numerów. */
const NO_SEEDS: ReadonlyMap<string, number> = new Map();

/**
 * Zapasowa dlugosc bloku meczu, gdy odpowiedz nie niesie `matchDurationMinutes`
 * (starszy backend w trakcie wdrozenia kroczacego) — domyslne 60 minut.
 */
const DEFAULT_SLOT_MINUTES = 60;

export interface PublicClubOverviewProps {
  /** Odpowiedz klubowa z lista rozgrywek i statystykami zawodnikow. */
  club: PublicClubCompetitions;
  /** Logo z oprawy ekranu — wygrywa z adresem z API. */
  logo: string | null;
  /** Tytul imprezy z oprawy ekranu. */
  screenTitle: string | null;
  /**
   * Otwiera liste turniejow (szuflade na waskich ekranach) — podpiete pod
   * kafelek „Turnieje w toku". Na szerokich ekranach lista jest zawsze
   * widoczna, wiec kafelek pozostaje tam bierny.
   */
  onShowTournaments?: () => void;
}

/**
 * Zakładka „Info ogólne" publicznego ekranu — centrum informacji o wszystkich
 * turniejach imprezy naraz: statystyki zbiorcze, mecze na żywo, nadchodzące,
 * ostatnie wyniki (z filtrami po turnieju i korcie), oś czasu kortów oraz karty
 * turniejów z mistrzami. Czysto odczytowa, jak cały ekran publiczny.
 */
export function PublicClubOverview({
  club,
  logo,
  screenTitle,
  onShowTournaments,
}: PublicClubOverviewProps) {
  const { t } = useTranslation('tournament');

  const competitions = club.competitions;
  const ids = useMemo(() => competitions.map((item) => item.id), [competitions]);

  const [timelineDate, setTimelineDate] = useState(todayIso());

  const matchesBatch = usePublicMatchesBatchQuery(ids);
  const schedulesBatch = usePublicCourtSchedulesBatchQuery(ids, timelineDate);

  // Wszystkie mecze imprezy w jednej liscie — mecz niesie competitionId.
  const allMatches = useMemo(
    () => [...matchesBatch.byId.values()].flatMap((response) => response.matches),
    [matchesBatch.byId],
  );

  // Jeden slownik nazwisk ze wszystkich odpowiedzi publicznych.
  const names = useMemo(() => {
    const merged = new Map<string, string>();
    const sources = [
      ...[...matchesBatch.byId.values()].map((response) => response.players),
      ...[...schedulesBatch.byId.values()].map((response) => response.players),
    ];
    for (const players of sources) {
      for (const [id, name] of buildPublicNameMap(players)) merged.set(id, name);
    }
    return merged;
  }, [matchesBatch.byId, schedulesBatch.byId]);

  const tournamentNames = useMemo(
    () => new Map(competitions.map((item) => [item.id, item.name])),
    [competitions],
  );

  // Wspolna pula kortow klubu sklejona z planow wszystkich rozgrywek.
  const courts = useMemo(() => {
    const byId = new Map<string, ApiClubCourt>();
    for (const schedule of schedulesBatch.byId.values()) {
      for (const entry of schedule.courts) {
        if (byId.has(entry.courtId)) continue;
        byId.set(entry.courtId, {
          id: entry.courtId,
          clubId: club.clubId,
          number: entry.number,
          name: entry.name,
          surface: entry.surface,
          blocks: entry.blocks.map((block, index) => ({
            id: `${entry.courtId}-${index}`,
            date: block.date,
            startTime: block.startTime,
            endTime: block.endTime,
            reason: block.reason,
          })),
          createdAt: '',
        });
      }
    }
    return [...byId.values()].sort((a, b) => a.number - b.number);
  }, [schedulesBatch.byId, club.clubId]);

  const courtNames = useMemo(
    () =>
      new Map(
        courts.map((court) => [
          court.id,
          court.name || t('publicScreen.court', { number: court.number }),
        ]),
      ),
    [courts, t],
  );

  // Sekcje live / nadchodzace / wyniki — zawsze ze wszystkich turniejow imprezy.
  const sections = useMemo(() => splitScreenMatches(allMatches), [allMatches]);

  // „Info ogolne” pokazuje wylacznie swieze wyniki — mecze starsze niz 14 dni
  // nie mowia juz nic o tym, co dzieje sie w klubie teraz.
  const recentMatches = useMemo(() => recentWithinDays(sections.recent), [sections.recent]);

  // Statystyki zbiorcze licza tylko turnieje, ktore jeszcze trwaja — zakonczone
  // zostaja na liscie i w sekcjach z wynikami, ale nie zawyzaja licznikow imprezy.
  const activeCompetitions = useMemo(
    () => competitions.filter((item) => item.status !== 'COMPLETED'),
    [competitions],
  );
  const activeMatches = useMemo(() => {
    const activeIds = new Set(activeCompetitions.map((item) => item.id));
    return allMatches.filter((match) => activeIds.has(match.competitionId ?? ''));
  }, [allMatches, activeCompetitions]);

  const progress = useMemo(() => screenProgress(activeMatches), [activeMatches]);
  const inProgressCount = competitions.filter((item) => item.status === 'IN_PROGRESS').length;
  const today = todayIso();
  const todayCount = useMemo(
    () => allMatches.filter((match) => match.scheduledDate === today).length,
    [allMatches, today],
  );

  /** Nazwiska jednej strony jako tekst — do osi czasu. */
  function sideText(matchIds: readonly string[]): string {
    if (matchIds.length === 0) return '—';
    return matchIds.map((id) => names.get(id) ?? '…').join(' / ');
  }

  // Os czasu: pseudo-planner sklejony z publicznych planow kortow.
  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const timeline: ScheduleTimeline = useMemo(() => {
    const empty: ScheduleTimeline = { startMinutes: 0, endMinutes: 0, tiles: [], nowMinutes: null };
    if (courts.length === 0) return empty;
    const scheduled: ApiPlannerMatch[] = [];
    for (const [competitionId, schedule] of schedulesBatch.byId) {
      for (const entry of schedule.courts) {
        for (const match of entry.matches) {
          scheduled.push({
            match,
            competitionName: tournamentNames.get(competitionId) ?? null,
            // Realne ustawienie rozgrywki (30/45/90...), nie sztywna godzina.
            durationMinutes: schedule.matchDurationMinutes ?? DEFAULT_SLOT_MINUTES,
          });
        }
      }
    }
    const planner: ApiMatchPlanner = {
      clubId: club.clubId,
      date: timelineDate,
      courts,
      competitions: [],
      scheduledMatches: scheduled,
      unscheduledMatches: [],
    };
    return buildScheduleTimeline(planner, sideText, { todayIso: today, nowMinutes });
    // sideText zalezy wylacznie od mapy nazwisk.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    schedulesBatch.byId,
    courts,
    names,
    tournamentNames,
    club.clubId,
    timelineDate,
    today,
    nowMinutes,
  ]);

  const gridCourts: ScheduleCourt[] = useMemo(
    () =>
      courts.map((court) => ({
        id: court.id,
        name: court.name || t('publicScreen.court', { number: court.number }),
        ...(court.surface ? { surface: court.surface } : {}),
      })),
    [courts, t],
  );

  /** Podpis meta wiersza meczu: faza w kolorze + turniej + kort. */
  function matchMeta(match: ApiMatch) {
    const round = roundLabel(match.round);
    const tournament = tournamentNames.get(match.competitionId ?? '');
    const court = match.courtId ? courtNames.get(match.courtId) : null;
    return (
      <span>
        {round && <span className={roundAccent(match.round) ?? undefined}>{round}</span>}
        {tournament && (
          <span className="text-fg-subtle">
            {round ? ' • ' : ''}
            {tournament}
          </span>
        )}
        {court && <span className="text-fg-subtle"> • {court}</span>}
      </span>
    );
  }

  // Fallback na czas wdrozen kroczacych — starszy backend nie zna jeszcze pola.
  const stats = club.playerStats ?? { totalPlayers: 0, femaleCount: 0, maleCount: 0 };

  return (
    <div className="flex min-w-0 flex-col gap-5">
      {/* Nagłówek imprezy */}
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="mb-1 text-xs font-bold tracking-[0.08em] text-brand-text uppercase">
            {t('publicScreen.overview.eyebrow')}
          </p>
          <h1 className="truncate text-3xl font-extrabold tracking-[-0.03em] text-fg-strong">
            {t('publicScreen.overview.tab')}
          </h1>
          <p className="mt-1 text-sm text-fg-muted">{t('publicScreen.overview.subtitle')}</p>
        </div>
        {inProgressCount > 0 && (
          <Badge tone="brand">
            {t('publicScreen.overview.activeBadge', { count: inProgressCount })}
          </Badge>
        )}
      </header>

      {/* Wizytówka imprezy: logo + miejsce */}
      <Card padding="md">
        <div className="flex flex-wrap items-center gap-5">
          <Avatar
            src={logo ?? club.clubLogoUrl}
            name={club.clubName ?? screenTitle ?? 'Klub'}
            size="xl"
            fit="contain"
            className="shrink-0"
          />
          <div className="flex min-w-0 flex-col gap-1">
            <p className="text-lg font-bold text-fg-strong">{club.clubName ?? screenTitle}</p>
            {club.clubCity && <p className="text-sm text-fg-muted">{club.clubCity}</p>}
          </div>
        </div>
      </Card>

      {/* Kafelki statystyk imprezy */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        <StatTile
          icon={<Trophy />}
          value={`${inProgressCount}/${activeCompetitions.length}`}
          label={t('publicScreen.overview.stats.tournaments')}
          {...(onShowTournaments
            ? // Na szerokich ekranach lista turniejów jest zawsze widoczna —
              // kafelek działa jako przycisk tylko poniżej progu `lg`.
              { onClick: onShowTournaments, className: 'lg:pointer-events-none' }
            : {})}
        />
        <StatTile
          icon={<Users />}
          value={stats.totalPlayers}
          label={t('publicScreen.overview.stats.players')}
        />
        <StatTile
          icon={<Venus />}
          value={stats.femaleCount}
          label={t('publicScreen.overview.stats.women')}
        />
        <StatTile
          icon={<Mars />}
          value={stats.maleCount}
          label={t('publicScreen.overview.stats.men')}
        />
        <StatTile
          icon={<LayoutGrid />}
          value={`${progress.resolved}/${progress.total}`}
          label={t('publicScreen.overview.stats.progress')}
        />
        <StatTile
          icon={<CalendarDays />}
          value={todayCount}
          label={t('publicScreen.overview.stats.today')}
        />
      </div>

      {matchesBatch.isPending && <Skeleton className="h-64 w-full" />}

      {!matchesBatch.isPending && (
        <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_22rem]">
          {/* Lewa kolumna: oś czasu, ostatnie wyniki, karty turniejów */}
          <div className="flex min-w-0 flex-col gap-5">
            {/* Oś czasu kortów */}
            <Card padding="lg" className="flex flex-col gap-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CardHeader title={t('publicScreen.overview.sections.timeline')} className="mb-0" />
                <div className="flex items-center gap-2">
                  <DateInput
                    inputSize="sm"
                    aria-label={t('publicScreen.overview.day')}
                    value={timelineDate}
                    onChange={(event) => setTimelineDate(event.target.value)}
                  />
                  <Button size="sm" variant="secondary" onClick={() => setTimelineDate(todayIso())}>
                    {t('publicScreen.overview.todayButton')}
                  </Button>
                </div>
              </div>
              {timeline.tiles.length === 0 ? (
                <p className="text-sm text-fg-muted">{t('publicScreen.overview.empty.timeline')}</p>
              ) : (
                <ScheduleGrid courts={gridCourts} timeline={timeline} />
              )}
            </Card>

            {/* Ostatnie wyniki */}
            <Card>
              <CardHeader title={t('publicScreen.overview.sections.recent')} />
              {recentMatches.length === 0 ? (
                <p className="text-sm text-fg-muted">{t('publicScreen.overview.empty.recent')}</p>
              ) : (
                <div className="grid gap-x-8 divide-y divide-border lg:grid-cols-2 lg:divide-y-0">
                  {recentMatches.slice(0, 8).map((match) => (
                    <MatchScoreRow
                      key={match.id}
                      match={toBracketMatchView(match, names, NO_SEEDS)}
                      showAvatars
                      metaPlacement="top"
                      className="lg:border-b lg:border-border"
                      meta={matchMeta(match)}
                    />
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* Prawa kolumna: na żywo + nadchodzące */}
          <div className="flex flex-col gap-5">
            <Card accent="brand">
              <CardHeader
                title={
                  sections.live.length > 0
                    ? `${t('publicScreen.overview.sections.live')} (${sections.live.length})`
                    : t('publicScreen.overview.sections.live')
                }
              />
              {sections.live.length === 0 ? (
                <p className="text-sm text-fg-muted">{t('publicScreen.overview.empty.live')}</p>
              ) : (
                <div className="flex flex-col divide-y divide-border">
                  {sections.live.map((match) => {
                    const court = match.courtId ? courtNames.get(match.courtId) : undefined;
                    const tournament = tournamentNames.get(match.competitionId ?? '');
                    return (
                      <LiveMatchCard
                        key={match.id}
                        match={toBracketMatchView(match, names, NO_SEEDS)}
                        courtLabel={court ?? tournament ?? ''}
                        showScore={false}
                        startedAt={match.startedAt}
                        {...(court && tournament ? { roundLabel: tournament } : {})}
                      />
                    );
                  })}
                </div>
              )}
            </Card>

            {club.schedulePublished && (
              <Card>
                <CardHeader title={t('publicScreen.overview.sections.upcoming')} />
                {sections.upcoming.length === 0 ? (
                  <p className="text-sm text-fg-muted">
                    {t('publicScreen.overview.empty.upcoming')}
                  </p>
                ) : (
                  <div className="flex flex-col divide-y divide-border">
                    {sections.upcoming.slice(0, 8).map((match) => (
                      <MatchScoreRow
                        key={match.id}
                        match={toBracketMatchView(match, names, NO_SEEDS)}
                        showAvatars
                        showScore={false}
                        meta={
                          <UpcomingMatchMeta
                            size="sm"
                            time={shortTime(match.scheduledTime) ?? roundLabel(match.round)}
                            place={
                              [
                                match.courtId ? courtNames.get(match.courtId) : null,
                                tournamentNames.get(match.competitionId ?? ''),
                              ]
                                .filter(Boolean)
                                .join(' • ') || null
                            }
                          />
                        }
                      />
                    ))}
                  </div>
                )}
              </Card>
            )}
          </div>
        </div>
      )}

      {competitions.length === 0 && (
        <EmptyState
          icon={<Trophy />}
          title={t('publicScreen.emptyTitle')}
          description={t('publicScreen.emptyDescription')}
          className="min-h-[16rem]"
        />
      )}
    </div>
  );
}
